"use client";

import { useState, useRef, useEffect, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { Share2, Copy, Download, ChevronDown } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ShareButtonsProps {
	selectedSlugs: string[];
	goal?: string;
	locale: string;
	channel: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const emptySubscribe = () => () => {};
const getHasNativeShareSnapshot = () => typeof navigator !== "undefined" && "share" in navigator;
const getOriginSnapshot = () => window.location.origin;
const getServerHasNativeShare = () => false;
const getServerOrigin = () => "";

// Both URLs are built through URL/URLSearchParams (like client.tsx's syncUrl) so the values
// are always percent-encoded — a goal carrying "&", "#" or a space used to produce a malformed
// link in the clipboard and in navigator.share. An empty goal is omitted rather than emitted
// as a bare "&goal=".
function buildShareUrl(
	origin: string,
	locale: string,
	channel: string,
	selectedSlugs: string[],
	goal?: string,
): string {
	if (!origin) return "";
	const url = new URL(`/${locale}/${channel}/stack-builder`, origin);
	url.searchParams.set("s", selectedSlugs.join(","));
	if (goal) url.searchParams.set("goal", goal);
	return url.toString();
}

function buildOgUrl(
	selectedSlugs: string[],
	goal: string | undefined,
	format: "og" | "story" | "post",
): string {
	const params = new URLSearchParams({ s: selectedSlugs.join(",") });
	if (goal) params.set("goal", goal);
	params.set("format", format);
	return `/api/og/stack?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Save Image Dropdown
// ---------------------------------------------------------------------------

interface SaveImageDropdownProps {
	selectedSlugs: string[];
	goal?: string;
	t: ReturnType<typeof useTranslations<"stackBuilder">>;
}

function SaveImageDropdown({ selectedSlugs, goal, t }: SaveImageDropdownProps) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	// Close on outside click
	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		if (open) {
			document.addEventListener("mousedown", handleClick);
		}
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	function handleDownload(format: "story" | "post") {
		posthog.capture("stack_builder_shared", {
			slugs: selectedSlugs,
			share_type: format === "story" ? "image_story" : "image_post",
		});
		const url = buildOgUrl(selectedSlugs, goal, format);
		const a = document.createElement("a");
		a.href = url;
		a.download = `bergdorfbio-stack-${format}.png`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		setOpen(false);
	}

	return (
		<div ref={ref} className="relative flex-1">
			<Button
				variant="outline-solid"
				size="sm"
				className="w-full"
				onClick={() => setOpen((prev) => !prev)}
				aria-expanded={open}
				aria-haspopup="true"
			>
				<Download className="h-3.5 w-3.5" aria-hidden="true" />
				{t("saveImage")}
				<ChevronDown
					className={cn("h-3 w-3 transition-transform duration-150", open && "rotate-180")}
					aria-hidden="true"
				/>
			</Button>

			{open && (
				<div
					className="absolute bottom-full left-0 z-50 mb-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md"
					role="menu"
				>
					<button
						type="button"
						className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
						role="menuitem"
						onClick={() => handleDownload("story")}
					>
						<span className="text-xs text-muted-foreground">1080×1920</span>
						{t("story")}
					</button>
					<button
						type="button"
						className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
						role="menuitem"
						onClick={() => handleDownload("post")}
					>
						<span className="text-xs text-muted-foreground">1080×1080</span>
						{t("post")}
					</button>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ShareButtons({ selectedSlugs, goal, locale, channel }: ShareButtonsProps) {
	const t = useTranslations("stackBuilder");
	const tCommon = useTranslations("common");
	const [copied, setCopied] = useState(false);
	const [showFallbackInput, setShowFallbackInput] = useState(false);
	const fallbackInputRef = useRef<HTMLInputElement>(null);

	// Browser-only signals via useSyncExternalStore: SSR + hydration render the
	// server snapshot (false / ""), then React re-renders once with the client
	// value - SSR/CSR markup stays identical (no React #418) without effects.
	const hasNativeShare = useSyncExternalStore(
		emptySubscribe,
		getHasNativeShareSnapshot,
		getServerHasNativeShare,
	);
	const origin = useSyncExternalStore(emptySubscribe, getOriginSnapshot, getServerOrigin);
	const shareUrl = buildShareUrl(origin, locale, channel, selectedSlugs, goal);

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			posthog.capture("stack_builder_shared", {
				slugs: selectedSlugs,
				share_type: "link",
			});
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setShowFallbackInput(true);
			setTimeout(() => {
				fallbackInputRef.current?.select();
			}, 50);
		}
	}

	function handleNativeShare() {
		posthog.capture("stack_builder_shared", {
			slugs: selectedSlugs,
			share_type: "native",
		});
		void navigator.share({
			title: t("meta.nativeShareTitle"),
			url: shareUrl,
		});
	}

	return (
		<div className="flex flex-col gap-2">
			{/* Fallback URL input (shown when clipboard API fails) */}
			{showFallbackInput && (
				<div className="flex gap-1">
					<input
						ref={fallbackInputRef}
						type="text"
						readOnly
						value={shareUrl}
						className="min-w-0 flex-1 rounded-md border border-border bg-muted px-2 py-1 text-xs text-foreground focus:outline-none"
						onClick={(e) => (e.target as HTMLInputElement).select()}
					/>
					<button
						type="button"
						className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
						onClick={() => setShowFallbackInput(false)}
						aria-label={tCommon("close")}
					>
						×
					</button>
				</div>
			)}

			{/* Share button row */}
			<div className="flex gap-2">
				{/* Copy URL */}
				<Button
					variant="outline-solid"
					size="sm"
					className={cn(
						"flex-1 transition-colors",
						copied && "border-green-500 text-green-600 dark:text-green-400",
					)}
					onClick={() => void handleCopy()}
				>
					<Copy className="h-3.5 w-3.5" aria-hidden="true" />
					{copied ? t("copied") : t("share")}
				</Button>

				{/* Native share (mobile only — rendered conditionally client-side) */}
				{hasNativeShare && (
					<Button
						variant="outline-solid"
						size="sm"
						className="shrink-0 px-2"
						onClick={handleNativeShare}
						aria-label={t("shareNativeAria")}
					>
						<Share2 className="h-3.5 w-3.5" aria-hidden="true" />
					</Button>
				)}

				{/* Save as Image dropdown */}
				<SaveImageDropdown selectedSlugs={selectedSlugs} goal={goal} t={t} />
			</div>
		</div>
	);
}
